import "@/App.css";
import { Suspense, lazy, useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { CartProvider } from "@/lib/cart";
import { trackPageview } from "@/lib/analytics";
import Home from "@/pages/Home";
import SellPage from "@/pages/SellPage";
import CatalogPage from "@/pages/CatalogPage";
import ProductPage from "@/pages/ProductPage";
import CartPage from "@/pages/CartPage";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import CollectionsIndex, { CollectionPage } from "@/pages/CollectionsPage";
import { TermsPage, PrivacyPage, RefundPolicyPage } from "@/pages/PolicyPages";
import FinancingPage from "@/pages/FinancingPage";
import ReturnRequestPage from "@/pages/ReturnRequestPage";
import WarrantyPage from "@/pages/WarrantyPage";

// GA4 page_view on every SPA route change (PostHog auto-captures pageviews
// itself; gtag is configured with send_page_view:false to avoid duplicates).
function AnalyticsTracker() {
  const { pathname, search } = useLocation();
  useEffect(() => {
    trackPageview(pathname + search);
  }, [pathname, search]);
  return null;
}

// Hide the shopper concierge on admin screens.
function ConciergeUnlessAdmin() {
  const { pathname } = useLocation();
  if (pathname.startsWith("/admin") || pathname === ADMIN_LOGIN_PATH) return null;
  return (
    <Suspense fallback={null}>
      <Concierge />
    </Suspense>
  );
}
import ContactPage from "@/pages/ContactPage";
import RingSizeGuide from "@/pages/RingSizeGuide";
import SellProcessPage from "@/pages/SellProcessPage";
import ShowroomsPage from "@/pages/ShowroomsPage";
import OurPromisePage from "@/pages/OurPromisePage";
import LabGrownGuidePage from "@/pages/LabGrownGuidePage";
import GoldKaratGuidePage from "@/pages/GoldKaratGuidePage";
import DiamondGuidePage from "@/pages/DiamondGuidePage";
import WatchGuidePage from "@/pages/WatchGuidePage";
// Admin panel + concierge are code-split: shoppers never download them (P2.9).
import AdminLogin from "@/pages/admin/AdminLogin";
import { ADMIN_LOGIN_PATH } from "@/lib/admin";
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminOrders = lazy(() => import("@/pages/admin/AdminOrders"));
const AdminMessages = lazy(() => import("@/pages/admin/AdminMessages"));
const AdminCustomers = lazy(() => import("@/pages/admin/AdminCustomers"));
const AdminPromos = lazy(() => import("@/pages/admin/AdminPromos"));
const AdminInventory = lazy(() => import("@/pages/admin/AdminInventory"));
const AdminPriceHolds = lazy(() => import("@/pages/admin/AdminPriceHolds"));
const AdminChannelsAmazon = lazy(() => import("@/pages/admin/AdminChannelsAmazon"));
const AdminEmailSettings = lazy(() => import("@/pages/admin/AdminEmailSettings"));
const AdminReviewSettings = lazy(() => import("@/pages/admin/AdminReviewSettings"));
const AdminPassword = lazy(() => import("@/pages/admin/AdminPassword"));
const AdminOrderDetail = lazy(() => import("@/pages/admin/AdminOrderDetail"));
const AdminOrderNew = lazy(() => import("@/pages/admin/AdminOrderNew"));
const AdminLogins = lazy(() => import("@/pages/admin/AdminLogins"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminReturns = lazy(() => import("@/pages/admin/AdminReturns"));
const AdminMerchandising = lazy(() => import("@/pages/admin/AdminMerchandising"));
const AdminQuotes = lazy(() => import("@/pages/admin/AdminQuotes"));
const AdminCustomerDetail = lazy(() => import("@/pages/admin/AdminCustomerDetail"));
const AdminSocialAccounts = lazy(() => import("@/pages/admin/AdminSocialAccounts"));
const AdminSocialContent = lazy(() => import("@/pages/admin/AdminSocialContent"));
const AdminSocialComposer = lazy(() => import("@/pages/admin/AdminSocialComposer"));
const AdminSocialCalendar = lazy(() => import("@/pages/admin/AdminSocialCalendar"));
const AdminSocialStudio = lazy(() => import("@/pages/admin/AdminSocialStudio"));
const AdminSocialMemory = lazy(() => import("@/pages/admin/AdminSocialMemory"));
const AdminMiDashboard = lazy(() => import("@/pages/admin/AdminMiDashboard"));
const AdminMiCompetitors = lazy(() => import("@/pages/admin/AdminMiCompetitors"));
const AdminMiProducts = lazy(() => import("@/pages/admin/AdminMiProducts"));
const Concierge = lazy(() => import("@/components/landing/Concierge").then((m) => ({ default: m.Concierge })));
import AccountPage from "@/pages/AccountPage";
import CartRecoverPage from "@/pages/CartRecoverPage";
import TrackOrderPage from "@/pages/TrackOrderPage";
import ReviewPage from "@/pages/ReviewPage";
import { PromoPopup } from "@/components/PromoPopup";
import { NotFound, Gone, LegacyProduct, LegacyCategory, LegacyStaticRedirect } from "@/pages/Legacy";

function App() {
  return (
    <div className="App">
      <CartProvider>
        <BrowserRouter>
          <AnalyticsTracker />
          <Suspense fallback={<div className="min-h-screen bg-[#F5F3EC]" />}>
            <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/sell" element={<SellPage />} />
            <Route path="/catalog" element={<CatalogPage />} />
            <Route path="/catalog/:sku" element={<ProductPage />} />
            <Route path="/cart" element={<CartPage />} />
            <Route path="/cart/recover/:token" element={<CartRecoverPage />} />
            <Route path="/track/:orderId" element={<TrackOrderPage />} />
            <Route path="/review/:orderId" element={<ReviewPage />} />
            <Route path="/payment/success" element={<PaymentSuccess />} />
            <Route path="/payment/cancel" element={<PaymentCancel />} />
            <Route path="/collections" element={<CollectionsIndex />} />
            <Route path="/collections/:slug" element={<CollectionPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/financing" element={<FinancingPage />} />
            <Route path="/returns" element={<ReturnRequestPage />} />
            <Route path="/warranty" element={<WarrantyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/ring-size-guide" element={<RingSizeGuide />} />
            <Route path="/sell-gold" element={<SellProcessPage kind="gold" />} />
            <Route path="/sell-diamonds" element={<SellProcessPage kind="diamonds" />} />
            <Route path="/showrooms" element={<ShowroomsPage />} />
            <Route path="/our-promise" element={<OurPromisePage />} />
            <Route path="/lab-grown-diamonds" element={<LabGrownGuidePage />} />
            <Route path="/gold-karat-guide" element={<GoldKaratGuidePage />} />
            <Route path="/diamond-buying-guide" element={<DiamondGuidePage />} />
            <Route path="/pre-owned-watch-guide" element={<WatchGuidePage />} />
            <Route path="/account" element={<AccountPage />} />
            <Route path={ADMIN_LOGIN_PATH} element={<AdminLogin />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/orders" element={<AdminOrders />} />
            <Route path="/admin/quotes" element={<AdminQuotes />} />
            <Route path="/admin/customers/:email" element={<AdminCustomerDetail />} />
            <Route path="/admin/returns" element={<AdminReturns />} />
            <Route path="/admin/reviews" element={<AdminReviews />} />
            <Route path="/admin/merchandising" element={<AdminMerchandising />} />
            <Route path="/admin/messages" element={<AdminMessages />} />
            <Route path="/admin/customers" element={<AdminCustomers />} />
            <Route path="/admin/promos" element={<AdminPromos />} />
            <Route path="/admin/inventory" element={<AdminInventory />} />
            <Route path="/admin/price-holds" element={<AdminPriceHolds />} />
            <Route path="/admin/channels/amazon" element={<AdminChannelsAmazon />} />
            <Route path="/admin/settings/emails" element={<AdminEmailSettings />} />
            <Route path="/admin/settings/reviews" element={<AdminReviewSettings />} />
            <Route path="/admin/orders/:sessionId" element={<AdminOrderDetail />} />
            <Route path="/admin/orders/new" element={<AdminOrderNew />} />
            <Route path="/admin/logins" element={<AdminLogins />} />
            {/* Phase A: the nav label is "Users" — make the matching URL work too (was a storefront 404) */}
            <Route path="/admin/users" element={<Navigate to="/admin/logins" replace />} />
            <Route path="/admin/password" element={<AdminPassword />} />
            <Route path="/admin/social/accounts" element={<AdminSocialAccounts />} />
            <Route path="/admin/social/content" element={<AdminSocialContent />} />
            <Route path="/admin/social/content/new" element={<AdminSocialComposer />} />
            <Route path="/admin/social/content/:id" element={<AdminSocialComposer />} />
            <Route path="/admin/social/calendar" element={<AdminSocialCalendar />} />
            <Route path="/admin/social/studio" element={<AdminSocialStudio />} />
            <Route path="/admin/social/memory" element={<AdminSocialMemory />} />
            <Route path="/admin/mi" element={<AdminMiDashboard />} />
            <Route path="/admin/mi/competitors" element={<AdminMiCompetitors />} />
            <Route path="/admin/mi/products" element={<AdminMiProducts />} />
            {/* Legacy WordPress/WooCommerce cleanup — mapped redirects, then retired pages */}
            <Route path="/product/*" element={<LegacyProduct />} />
            <Route path="/product-category/*" element={<LegacyCategory />} />
            <Route path="/shop/*" element={<LegacyStaticRedirect to="/catalog" label="Shop" />} />
            <Route path="/my-account/*" element={<LegacyStaticRedirect to="/account" label="My Account" />} />
            <Route path="/checkout/*" element={<LegacyStaticRedirect to="/cart" label="Checkout" />} />
            <Route path="/wishlist/*" element={<LegacyStaticRedirect to="/account" label="Wishlist" />} />
            <Route path="/tag/*" element={<Gone />} />
            <Route path="/author/*" element={<Gone />} />
            <Route path="/category/*" element={<Gone />} />
            <Route path="/attachment/*" element={<Gone />} />
            <Route path="/wp-json/*" element={<Gone />} />
            <Route path="/feed/*" element={<Gone />} />
            <Route path="/comments/*" element={<Gone />} />
            <Route path="/xmlrpc.php" element={<Gone />} />
            <Route path="/wp-admin/*" element={<Gone />} />
            <Route path="/wp-content/*" element={<Gone />} />
            <Route path="/sample-page/*" element={<Gone />} />
            <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <ConciergeUnlessAdmin />
          <PromoPopup />
        </BrowserRouter>
      </CartProvider>
      <Toaster position="top-center" richColors />
    </div>
  );
}

export default App;
